import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Share2, Sparkles, ArrowRight, ChevronRight, Settings,
  Send, Clock, CheckCircle, ExternalLink, Eye, XCircle, CalendarClock,
  BarChart3, Wand2, TrendingUp, Rocket,
  Heart, MessageCircle, Bookmark, Users, Loader2
} from 'lucide-react';
import { TAGLINES, LABELS, EVENT_LABELS, formatPastDate } from '../constants/dashboard';
import { PUBLICATION_CHANNELS, DEFAULT_PUBLICATION_EVENTS } from '../constants/settings';
import { STOCK_STATUS } from '../constants/vehicleStatus';
import { dashboardApi } from '../services/dashboardApi';
import PublicationPreviewModal from '../components/PublicationPreviewModal';
import api from '../services/api';

// ---------------------------------------------------------------------------
// Shared helpers
// ---------------------------------------------------------------------------
const Section = ({ children, className = '' }) => (
  <section className={`mb-16 ${className}`}>{children}</section>
);

const SectionTitle = ({ children, sub }) => (
  <div className="text-center mb-10">
    <h2 className="text-2xl md:text-3xl font-bold text-primary mb-3">{children}</h2>
    {sub && <p className="text-secondary max-w-xl mx-auto">{sub}</p>}
  </div>
);

// --- SVG platform icon ---
const PlatformIcon = ({ platform, className }) => {
  if (platform === 'instagram') {
    return (
      <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
        <circle cx="12" cy="12" r="5" />
        <circle cx="17.5" cy="6.5" r="1.5" fill="currentColor" stroke="none" />
      </svg>
    );
  }
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
    </svg>
  );
};

// ---------------------------------------------------------------------------
// Onboarding data
// ---------------------------------------------------------------------------
const PILLARS = [
  {
    icon: Wand2,
    title: 'Contenu IA automatique',
    desc: 'Générez des textes et visuels percutants en un clic grâce à l\'intelligence artificielle, adaptés à chaque réseau.',
  },
  {
    icon: Share2,
    title: 'Publication multi-plateforme',
    desc: 'Publiez simultanément sur Instagram (Feed & Story) et Facebook depuis une seule interface.',
  },
  {
    icon: BarChart3,
    title: 'Suivi et planification',
    desc: 'Programmez vos publications à l\'avance et suivez l\'historique de toutes vos diffusions.',
  },
];

const STEPS = [
  { num: 1, title: 'Connectez vos comptes', desc: 'Liez Instagram et Facebook à Carmin en quelques clics.' },
  { num: 2, title: 'Ajoutez des véhicules', desc: 'Importez votre stock — le contenu IA est généré automatiquement.' },
  { num: 3, title: 'Publiez en un clic', desc: 'Choisissez les canaux et publiez ou programmez vos posts.' },
  { num: 4, title: 'Suivez les résultats', desc: 'Consultez l\'historique et optimisez votre stratégie sociale.' },
];

// ---------------------------------------------------------------------------
// Onboarding component
// ---------------------------------------------------------------------------
const SocialOnboarding = () => {
  const tagline = TAGLINES.social.fr;

  return (
    <div className="max-w-5xl mx-auto pb-16">
      {/* HERO */}
      <Section>
        <div className="relative rounded-2xl overflow-hidden bg-gradient-to-br from-carmin/[0.08] via-dark-card to-dark-surface border border-carmin/20 p-8 md:p-12">
          <div className="absolute top-0 right-0 w-64 h-64 bg-carmin/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/4" />
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-pink-500/5 rounded-full blur-3xl translate-y-1/2 -translate-x-1/4" />

          <div className="relative z-10 max-w-2xl">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-carmin/10 border border-carmin/20 mb-6">
              <Sparkles className="h-3.5 w-3.5 text-carmin" />
              <span className="text-xs font-medium text-carmin-light">Réseaux Sociaux</span>
            </div>

            <h1 className="text-3xl md:text-4xl font-bold text-primary mb-4 leading-tight">
              {tagline.slogan}
            </h1>

            <p className="text-lg text-secondary mb-8 leading-relaxed">
              {tagline.description}
            </p>

            <Link
              to="/settings?tab=social"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-carmin hover:bg-carmin-dark text-white font-semibold transition-colors"
            >
              Connecter mes réseaux
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </Section>

      {/* 3 PILLARS */}
      <Section>
        <SectionTitle sub="Tout ce dont vous avez besoin pour briller sur les réseaux.">
          Pourquoi publier avec Carmin ?
        </SectionTitle>

        <div className="grid md:grid-cols-3 gap-5">
          {PILLARS.map((p) => (
            <div
              key={p.title}
              className="rounded-xl border border-carmin/20 bg-carmin/[0.04] p-6 transition-all hover:scale-[1.02] hover:shadow-lg"
            >
              <div className="w-10 h-10 rounded-lg bg-carmin/10 flex items-center justify-center mb-4">
                <p.icon className="h-5 w-5 text-carmin" />
              </div>
              <h3 className="text-lg font-semibold text-primary mb-2">{p.title}</h3>
              <p className="text-sm text-secondary leading-relaxed">{p.desc}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* HOW IT WORKS */}
      <Section>
        <SectionTitle sub="De la connexion à la première publication, en quelques minutes.">
          Comment ça marche
        </SectionTitle>

        <div className="relative">
          <div className="hidden md:block absolute top-8 left-0 right-0 h-0.5 bg-dark-border" style={{ left: '12.5%', right: '12.5%' }} />

          <div className="grid md:grid-cols-4 gap-6">
            {STEPS.map((s, idx) => (
              <div key={s.num} className="relative text-center">
                <div className="relative z-10 w-16 h-16 rounded-full bg-dark-card border-2 border-carmin/40 flex items-center justify-center mx-auto mb-4">
                  <span className="text-xl font-bold text-carmin">{s.num}</span>
                </div>
                <h4 className="text-sm font-semibold text-primary mb-1.5">{s.title}</h4>
                <p className="text-xs text-muted leading-relaxed">{s.desc}</p>
                {idx < STEPS.length - 1 && (
                  <ChevronRight className="md:hidden h-5 w-5 text-dark-border mx-auto mt-4" />
                )}
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* FINAL CTA */}
      <Section className="mb-0">
        <div className="rounded-xl border border-carmin/20 bg-gradient-to-br from-carmin/[0.06] to-dark-card p-8 md:p-10 text-center">
          <Share2 className="h-12 w-12 text-carmin/60 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-primary mb-2">Prêt à publier ?</h3>
          <p className="text-secondary mb-6 max-w-md mx-auto">
            Connectez vos comptes Instagram et Facebook pour commencer à publier du contenu IA en un clic.
          </p>
          <Link
            to="/settings?tab=social"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-carmin hover:bg-carmin-dark text-white font-semibold transition-colors"
          >
            <Settings className="h-4 w-4" />
            Connecter mes réseaux
          </Link>
        </div>
      </Section>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Dashboard components
// ---------------------------------------------------------------------------

const TabButton = ({ label, count, isActive, onClick, color }) => (
  <button
    onClick={onClick}
    className={`flex-shrink-0 px-3 py-1.5 rounded-lg text-xs font-medium transition-all whitespace-nowrap ${
      isActive
        ? 'bg-carmin text-white'
        : 'bg-dark-hover text-secondary hover:text-primary hover:bg-dark-surface'
    }`}
  >
    {label}
    {count > 0 && (
      <span className={`ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold ${
        isActive ? 'bg-theme-inverted/20 text-primary' : `${color || 'bg-carmin/20 text-carmin-light'}`
      }`}>
        {count}
      </span>
    )}
  </button>
);

const ReadyVehicleCard = ({ vehicle, publications, activeChannels, eventType, onPreview, onPublishChannel, onPublishBatch }) => {
  const DONE_STATUSES = ['published', 'scheduled', 'queued', 'publishing'];
  const vehiclePubs = publications.filter(p => p.vehicle_id === vehicle.id && DONE_STATUSES.includes(p.status));
  const isChannelDone = (ch) =>
    vehiclePubs.some(p => p.platform === ch.platform && p.format === ch.format);
  const getChannelStatus = (ch) => {
    const pub = vehiclePubs.find(p => p.platform === ch.platform && p.format === ch.format);
    return pub?.status || null;
  };

  const unpublishedChannels = activeChannels.filter(ch => !isChannelDone(ch));
  const allDone = activeChannels.length > 0 && unpublishedChannels.length === 0;
  const hasProcessing = activeChannels.some(ch => {
    const s = getChannelStatus(ch);
    return s === 'queued' || s === 'publishing';
  });

  const [selectedChannels, setSelectedChannels] = useState([]);
  const [publishingChannel, setPublishingChannel] = useState(null);
  const [batchPublishing, setBatchPublishing] = useState(false);

  const toggleChannel = (chId) => {
    setSelectedChannels(prev =>
      prev.includes(chId) ? prev.filter(id => id !== chId) : [...prev, chId]
    );
  };

  const selectAll = () => {
    setSelectedChannels(unpublishedChannels.map(ch => ch.id));
  };

  const eventLabel = EVENT_LABELS[eventType] || EVENT_LABELS.new_vehicle;
  const selectedCount = selectedChannels.filter(id => {
    const ch = activeChannels.find(c => c.id === id);
    return ch && !isChannelDone(ch);
  }).length;

  return (
    <div className={`w-full bg-dark-hover rounded-xl border overflow-hidden transition-all flex flex-col ${
      allDone && !hasProcessing ? 'border-dark-border opacity-40' : 'border-dark-border'
    }`}>
      {/* Image */}
      <div className="w-full h-32 sm:h-40 bg-dark-surface flex-shrink-0">
        {vehicle.images?.[0] ? (
          <img src={vehicle.images[0]} alt="" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-dimmed text-xs">-</div>
        )}
      </div>

      <div className="p-3 gap-3 flex-1 flex flex-col">
        {/* Vehicle info + event badge */}
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="text-sm font-semibold text-primary truncate">{vehicle.make} {vehicle.model}</p>
            {(vehicle.year || vehicle.mileage || vehicle.price) && (
              <p className="text-[11px] text-muted mt-0.5">
                {[vehicle.year, vehicle.mileage ? `${Math.round(vehicle.mileage / 1000)}k km` : null, vehicle.price ? `${vehicle.price.toLocaleString('fr-FR')}€` : null].filter(Boolean).join(' · ')}
              </p>
            )}
          </div>
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold flex-shrink-0 ${eventLabel.bg} ${eventLabel.color} ${eventLabel.border} border`}>
            <span className="w-1.5 h-1.5 rounded-full bg-current" />
            {eventLabel.label}
          </span>
        </div>

        {/* Channel tiles grid */}
        <div className="grid grid-cols-2 gap-1.5">
          {activeChannels.map(ch => {
            const done = isChannelDone(ch);
            const chStatus = getChannelStatus(ch);
            const isProcessing = chStatus === 'queued' || chStatus === 'publishing';
            const checked = selectedChannels.includes(ch.id);
            return (
              <div
                key={ch.id}
                className={`rounded-lg border p-2 transition-all ${
                  done
                    ? 'border-dark-border bg-dark-surface opacity-50'
                    : checked
                      ? 'border-carmin/30 bg-carmin/5'
                      : 'border-dark-border bg-dark-surface'
                }`}
              >
                <div className="flex items-center gap-1.5 mb-2">
                  {!done && (
                    <button
                      onClick={(e) => { e.stopPropagation(); toggleChannel(ch.id); }}
                      className={`w-3.5 h-3.5 rounded border flex items-center justify-center flex-shrink-0 transition-colors ${
                        checked ? 'bg-carmin border-carmin' : 'border-gray-500 hover:border-gray-400'
                      }`}
                    >
                      {checked && <CheckCircle className="w-2.5 h-2.5 text-white" />}
                    </button>
                  )}
                  <PlatformIcon platform={ch.platform} className="w-3 h-3 text-muted flex-shrink-0" />
                  <span className={`text-[11px] font-medium truncate ${done ? 'text-muted' : 'text-secondary'}`}>
                    {ch.shortLabel}
                  </span>
                  {done && !isProcessing && <CheckCircle className="w-3 h-3 text-emerald-400 ml-auto flex-shrink-0" />}
                  {isProcessing && <span className="w-3 h-3 border-2 border-carmin/30 border-t-carmin rounded-full animate-spin ml-auto flex-shrink-0" />}
                </div>
                {done ? (
                  <div className="text-center">
                    <span className="text-[10px] text-muted">{isProcessing ? 'En cours...' : 'Publié'}</span>
                  </div>
                ) : (
                  <div className="flex gap-1">
                    <button
                      onClick={(e) => { e.stopPropagation(); onPreview(vehicle, ch.platform, ch.format); }}
                      className="flex-1 flex items-center justify-center gap-1 py-1.5 rounded-md bg-dark-hover hover:bg-dark-surface text-secondary hover:text-primary text-[10px] font-medium transition-colors"
                    >
                      <Eye className="w-3 h-3" />
                      Voir
                    </button>
                    <button
                      disabled={publishingChannel === ch.id || batchPublishing}
                      onClick={async (e) => {
                        e.stopPropagation();
                        setPublishingChannel(ch.id);
                        try {
                          await onPublishChannel(vehicle, ch.platform, ch.format, eventType);
                        } finally {
                          setPublishingChannel(null);
                        }
                      }}
                      className="flex-1 flex items-center justify-center gap-1 py-1.5 rounded-md bg-carmin/10 hover:bg-carmin/20 text-carmin hover:text-carmin-light text-[10px] font-medium transition-colors disabled:opacity-50"
                    >
                      {publishingChannel === ch.id ? (
                        <span className="w-3 h-3 border-2 border-carmin/30 border-t-carmin rounded-full animate-spin" />
                      ) : (
                        <Rocket className="w-3 h-3" />
                      )}
                      {publishingChannel === ch.id ? '...' : 'Pub.'}
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Batch CTA */}
        {!allDone && (
          <div className="space-y-1.5 mt-auto">
            {selectedCount === 0 && unpublishedChannels.length > 1 && (
              <button
                onClick={selectAll}
                className="w-full py-1.5 text-[11px] font-medium text-secondary hover:text-primary transition-colors"
              >
                Tout sélectionner ({unpublishedChannels.length})
              </button>
            )}
            <button
              onClick={async () => {
                const channels = activeChannels.filter(ch => selectedChannels.includes(ch.id) && !isChannelDone(ch));
                if (channels.length > 0) {
                  setBatchPublishing(true);
                  try {
                    await onPublishBatch(vehicle, channels, eventType);
                  } finally {
                    setBatchPublishing(false);
                  }
                }
              }}
              disabled={selectedCount === 0 || batchPublishing}
              className="w-full flex items-center justify-center gap-1.5 py-2 rounded-lg bg-carmin hover:bg-carmin-dark text-white text-xs font-semibold transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            >
              {batchPublishing ? (
                <span className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <Rocket className="w-3.5 h-3.5" />
              )}
              {batchPublishing
                ? 'Publication en cours...'
                : selectedCount === unpublishedChannels.length
                  ? `Tout publier · ${selectedCount}`
                  : selectedCount > 0
                    ? `Publier la sélection · ${selectedCount}`
                    : 'Sélectionnez des canaux'
              }
            </button>
          </div>
        )}
        {allDone && (
          <div className="flex items-center justify-center gap-1.5 py-2 text-[11px] text-muted mt-auto">
            {hasProcessing ? (
              <>
                <span className="w-3.5 h-3.5 border-2 border-carmin/30 border-t-carmin rounded-full animate-spin" />
                Publication en cours...
              </>
            ) : (
              <>
                <CheckCircle className="w-3.5 h-3.5" />
                Tout publié
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const ScheduledPubCard = ({ pub, vehicle, onPreview, onCancel, onReschedule }) => {
  const [showReschedule, setShowReschedule] = useState(false);
  const [newDate, setNewDate] = useState('');
  const [newTime, setNewTime] = useState('');
  const [saving, setSaving] = useState(false);

  const handleReschedule = async () => {
    if (!newDate || !newTime) return;
    setSaving(true);
    try {
      await onReschedule(pub.id, `${newDate}T${newTime}:00`);
      setShowReschedule(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-dark-hover rounded-lg border border-dark-border overflow-hidden">
      <button
        onClick={() => vehicle && onPreview(vehicle)}
        className="flex items-center gap-3 p-3 w-full text-left hover:bg-dark-surface transition-colors"
      >
        <div className="w-9 h-9 rounded-lg overflow-hidden bg-dark-surface flex-shrink-0">
          {pub.content?.images?.[0] ? (
            <img src={pub.content.images[0]} alt="" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-muted text-xs">-</div>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-primary truncate">
            {vehicle ? `${vehicle.make} ${vehicle.model}` : 'Véhicule'}
          </p>
          <div className="flex items-center gap-2 text-xs text-muted">
            <PlatformIcon platform={pub.platform} className="w-3 h-3" />
            <span>{pub.format === 'story' ? LABELS.story : LABELS.feed}</span>
            <span className="text-dimmed">·</span>
            <span>{new Date(pub.scheduled_at).toLocaleString('fr-BE', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
          </div>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          <span
            role="button"
            onClick={(e) => { e.stopPropagation(); setShowReschedule(!showReschedule); }}
            className="p-1.5 rounded-lg text-muted hover:text-amber-400 hover:bg-amber-500/10 transition-colors"
            title="Reprogrammer"
          >
            <CalendarClock className="w-4 h-4" />
          </span>
          <span
            role="button"
            onClick={(e) => { e.stopPropagation(); onCancel(pub.id); }}
            className="p-1.5 rounded-lg text-muted hover:text-red-400 hover:bg-red-500/10 transition-colors"
            title="Annuler"
          >
            <XCircle className="w-4 h-4" />
          </span>
        </div>
      </button>
      {showReschedule && (
        <div className="px-3 pb-3 pt-1 border-t border-dark-border flex items-center gap-2">
          <input
            type="date"
            value={newDate}
            onChange={(e) => setNewDate(e.target.value)}
            className="flex-1 bg-dark-surface border border-dark-border rounded-lg px-2 py-1.5 text-xs text-primary"
          />
          <input
            type="time"
            value={newTime}
            onChange={(e) => setNewTime(e.target.value)}
            className="w-24 bg-dark-surface border border-dark-border rounded-lg px-2 py-1.5 text-xs text-primary"
          />
          <button
            onClick={handleReschedule}
            disabled={!newDate || !newTime || saving}
            className="px-3 py-1.5 bg-amber-500 text-white text-xs font-medium rounded-lg hover:bg-amber-600 disabled:opacity-40 transition-colors"
          >
            {saving ? '...' : 'OK'}
          </button>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Dashboard component
// ---------------------------------------------------------------------------
const SocialDashboard = ({ oauthAccounts, settings }) => {
  const [vehicles, setVehicles] = useState([]);
  const [publications, setPublications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(null); // set after data load
  const [socialFilter, setSocialFilter] = useState('all');
  const [previewVehicle, setPreviewVehicle] = useState(null);
  const [historyLimit, setHistoryLimit] = useState(20);
  const [publishLimit, setPublishLimit] = useState(6);
  const [eventFilter, setEventFilter] = useState('all');
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const [vRes, pRes] = await Promise.all([
        api.get('/vehicles'),
        api.get('/publications'),
      ]);
      setVehicles(vRes.data.vehicles || []);
      setPublications(pRes.data.publications || []);
    } catch {
      // silently fail
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // Auto-refresh while publications are processing (queued/publishing)
  useEffect(() => {
    const hasProcessing = publications.some(p => p.status === 'queued' || p.status === 'publishing');
    if (!hasProcessing) return;
    const interval = setInterval(() => loadData(), 5000);
    return () => clearInterval(interval);
  }, [publications, loadData]);

  // Fetch stats on mount (needed for KPI cards + stats tab)
  useEffect(() => {
    setStatsLoading(true);
    dashboardApi.getSocialStats(30).then(data => {
      setStats(data);
    }).finally(() => setStatsLoading(false));
  }, []);

  const pubEvents = settings?.publication_events || DEFAULT_PUBLICATION_EVENTS;
  const getActiveChannels = (eventType) => {
    const cfg = pubEvents[eventType] || {};
    return PUBLICATION_CHANNELS.filter(ch => cfg[ch.id]);
  };

  const getVehicleEventType = (vehicle) => {
    if (vehicle.stock_status === STOCK_STATUS.SOLD) return 'sold_vehicle';
    if (vehicle.price_reduced) return 'price_reduction';
    return 'new_vehicle';
  };

  // Build maps and lists
  const vehicleMap = {};
  vehicles.forEach(v => { vehicleMap[v.id] = v; });

  const readyVehicles = vehicles
    .filter(v => v.status === 'ready' && (v.generated_content || v.sold_content) && v.images?.length > 0);

  const pendingReadyVehicles = readyVehicles.filter(vehicle => {
    const eventType = getVehicleEventType(vehicle);
    const activeChannels = getActiveChannels(eventType);
    if (activeChannels.length === 0) return false;
    const handledPubs = publications.filter(
      p => p.vehicle_id === vehicle.id && (p.status === 'published' || p.status === 'scheduled')
    );
    const isHandled = (ch) => handledPubs.some(p => p.platform === ch.platform && p.format === ch.format);
    return !activeChannels.every(ch => isHandled(ch));
  });

  const scheduledPubs = publications.filter(p => p.status === 'scheduled');
  const publishedPubs = publications
    .filter(p => p.status === 'published')
    .sort((a, b) => new Date(b.published_at) - new Date(a.published_at));

  // Stats — current 30 days vs previous 30 days
  const now = new Date();
  const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
  const recentPubs = publishedPubs.filter(p => new Date(p.published_at) >= thirtyDaysAgo);

  // Filter vehicles by event type
  const filteredByEvent = eventFilter === 'all'
    ? pendingReadyVehicles
    : pendingReadyVehicles.filter(v => getVehicleEventType(v) === eventFilter);

  const eventCounts = {
    all: pendingReadyVehicles.length,
    new_vehicle: pendingReadyVehicles.filter(v => getVehicleEventType(v) === 'new_vehicle').length,
    price_reduction: pendingReadyVehicles.filter(v => getVehicleEventType(v) === 'price_reduction').length,
    sold_vehicle: pendingReadyVehicles.filter(v => getVehicleEventType(v) === 'sold_vehicle').length,
  };

  const filteredHistory = socialFilter === 'all'
    ? publishedPubs
    : publishedPubs.filter(p => p.platform === socialFilter);

  // Default tab
  const computedTab = activeTab || (pendingReadyVehicles.length > 0 ? 'publish' : scheduledPubs.length > 0 ? 'scheduled' : 'history');

  const handleCancelScheduled = async (pubId) => {
    try {
      await dashboardApi.cancelPublication(pubId);
      setPublications(prev => prev.filter(p => p.id !== pubId));
    } catch { /* */ }
  };

  const handleRescheduleScheduled = async (pubId, newDate) => {
    try {
      await dashboardApi.reschedulePublication(pubId, newDate);
      setPublications(prev => prev.map(p =>
        p.id === pubId ? { ...p, scheduled_at: newDate } : p
      ));
    } catch { /* */ }
  };

  const handlePublished = () => {
    setPreviewVehicle(null);
    loadData();
  };

  const handlePublishChannel = async (vehicle, platform, format, evtType) => {
    try {
      await api.post('/publications', {
        vehicle_id: vehicle.id,
        platform,
        format,
        event_type: evtType,
      });
      await loadData();
    } catch (err) {
      const msg = err.response?.data?.detail || 'Erreur lors de la publication';
      console.error('Publish channel error:', msg);
      alert(msg);
    }
  };

  const handlePublishBatch = async (vehicle, channels, evtType) => {
    try {
      await api.post('/publications/batch', {
        vehicle_id: vehicle.id,
        event_type: evtType,
      });
      await loadData();
    } catch (err) {
      const msg = err.response?.data?.detail || 'Erreur lors de la publication groupée';
      console.error('Batch publish error:', msg);
      alert(msg);
    }
  };

  const handlePreviewChannel = (vehicle, platform, format) => {
    setPreviewVehicle({ vehicle, platform, format });
  };

  if (loading) {
    return (
      <div className="max-w-5xl mx-auto pb-16">
        <div className="flex items-center justify-center py-20">
          <div className="h-6 w-6 border-2 border-carmin/30 border-t-carmin rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto pb-16 space-y-6">
      {/* HEADER */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-carmin/10 flex items-center justify-center">
          <Share2 className="h-5 w-5 text-carmin" />
        </div>
        <h1 className="text-xl font-bold text-primary">{LABELS.sectionSocial}</h1>
      </div>

      {/* KPI cards - 2 columns */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {/* Left: Engagement */}
        <div className="bg-dark-card rounded-xl border border-dark-border p-4 flex flex-col">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 rounded-lg bg-blue-500/10 flex items-center justify-center">
              <TrendingUp className="h-4 w-4 text-blue-400" />
            </div>
            <div>
              <span className="text-sm font-medium text-primary">Engagement</span>
              <span className="text-xs text-muted ml-1.5">30 jours</span>
            </div>
          </div>
          {statsLoading ? (
            <div className="flex items-center gap-2 py-1">
              <Loader2 className="h-3.5 w-3.5 text-muted animate-spin" />
              <span className="text-xs text-muted">Chargement...</span>
            </div>
          ) : stats && stats.total_publications > 0 ? (
            <div className="flex-1">
              {(() => {
                const total = stats.totals.likes + stats.totals.comments;
                const rate = stats.totals.impressions > 0
                  ? ((total / stats.totals.impressions) * 100)
                  : 0;
                const message = total === 0
                  ? 'Vos publications sont en ligne. Les premières interactions arrivent !'
                  : rate >= 3
                    ? 'Excellente dynamique ! Votre contenu engage vraiment votre audience.'
                    : total >= 10
                      ? 'Bonne progression ! Vos publications génèrent de l\'engagement.'
                      : 'Votre présence se construit, continuez à publier régulièrement.';
                return (
                  <>
                    <p className="text-[13px] text-gray-300 mb-2.5 leading-relaxed">
                      {message}
                    </p>
                    {rate > 0 && (
                      <div className="flex items-center gap-2 mb-1.5">
                        <div className="flex-1 h-1.5 bg-dark-surface rounded-full overflow-hidden">
                          <div
                            className="h-full rounded-full bg-blue-400/60"
                            style={{ width: `${Math.min(rate * 10, 100)}%` }}
                          />
                        </div>
                        <span className="text-xs text-gray-400 font-medium">
                          {rate.toFixed(1)}%
                        </span>
                      </div>
                    )}
                    <p className="text-xs text-gray-500">
                      {total} interaction{total > 1 ? 's' : ''} · {stats.total_publications} publication{stats.total_publications > 1 ? 's' : ''}
                    </p>
                  </>
                );
              })()}
            </div>
          ) : (
            <p className="text-[13px] text-gray-400 flex-1">Publiez du contenu pour voir vos performances ici.</p>
          )}
          <button
            onClick={() => setActiveTab('stats')}
            className="mt-3 flex items-center justify-center gap-1.5 w-full py-2.5 text-xs font-medium text-blue-400 bg-blue-500/10 rounded-lg hover:bg-blue-500/15 transition-colors"
          >
            <BarChart3 className="w-3.5 h-3.5" />
            Voir les statistiques
          </button>
        </div>

        {/* Right: Configuration summary */}
        <div className="bg-dark-card rounded-xl border border-dark-border p-4 flex flex-col">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 rounded-lg bg-blue-500/10 flex items-center justify-center">
              <Settings className="h-4 w-4 text-blue-400" />
            </div>
            <span className="text-sm font-medium text-primary">Publication auto</span>
          </div>
          {/* Event × Channel matrix */}
          <div className="flex-1">
            <table className="w-full">
              <thead>
                <tr>
                  <th />
                  {PUBLICATION_CHANNELS.map(ch => (
                    <th key={ch.id} className="text-center pb-2 px-1">
                      <span className="inline-flex items-center gap-1 text-xs text-gray-400 font-medium">
                        <PlatformIcon platform={ch.platform} className="w-3 h-3" />
                        {ch.shortLabel}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {Object.entries(pubEvents).map(([eventId, channels]) => {
                  const ev = EVENT_LABELS[eventId];
                  if (!ev) return null;
                  return (
                    <tr key={eventId}>
                      <td className="py-1.5 pr-3">
                        <span className="text-[13px] text-gray-300">{ev.label}</span>
                      </td>
                      {PUBLICATION_CHANNELS.map(ch => (
                        <td key={ch.id} className="text-center py-1.5">
                          {channels[ch.id] ? (
                            <CheckCircle className="w-4 h-4 text-blue-400/70 mx-auto" />
                          ) : (
                            <span className="block w-4 h-4 mx-auto rounded-full border border-gray-700" />
                          )}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <Link
            to="/settings?tab=social"
            className="mt-3 flex items-center justify-center gap-1.5 w-full py-2.5 text-xs font-medium text-blue-400 bg-blue-500/10 rounded-lg hover:bg-blue-500/15 transition-colors"
          >
            <Settings className="w-3.5 h-3.5" />
            Modifier
          </Link>
        </div>
      </div>

      {/* TABS + CONTENT */}
      <div className="bg-dark-card rounded-xl border border-dark-border p-5">
        {/* Tabs */}
        <div className="flex gap-1.5 overflow-x-auto scrollbar-hide -mx-1 px-1 pb-0.5 mb-4">
          <TabButton
            label={LABELS.tabPublish}
            count={pendingReadyVehicles.length}
            isActive={computedTab === 'publish'}
            onClick={() => setActiveTab('publish')}
            color="bg-carmin/20 text-carmin-light"
          />
          <TabButton
            label={LABELS.tabScheduled}
            count={scheduledPubs.length}
            isActive={computedTab === 'scheduled'}
            onClick={() => setActiveTab('scheduled')}
            color="bg-amber-500/20 text-amber-400"
          />
          <TabButton
            label={LABELS.tabHistory}
            count={publishedPubs.length}
            isActive={computedTab === 'history'}
            onClick={() => setActiveTab('history')}
          />
          <TabButton
            label="Statistiques"
            count={0}
            isActive={computedTab === 'stats'}
            onClick={() => setActiveTab('stats')}
            color="bg-purple-500/20 text-purple-400"
          />
        </div>

        <div className="min-h-[180px]">
          {/* À PUBLIER */}
          {computedTab === 'publish' && (
            <div>
              {pendingReadyVehicles.length === 0 ? (
                <div className="relative overflow-hidden rounded-xl border border-dashed border-carmin/20 bg-gradient-to-br from-carmin/[0.04] to-dark-hover p-8 text-center">
                  <Send className="h-10 w-10 text-carmin/40 mx-auto mb-3" />
                  <p className="text-sm font-medium text-primary mb-1">{LABELS.socialEmptyTitle}</p>
                  <p className="text-xs text-muted mb-4 max-w-xs mx-auto">{LABELS.socialEmptyDesc}</p>
                  <Link
                    to="/stock"
                    className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium bg-carmin text-white rounded-lg hover:bg-carmin-dark transition-colors"
                  >
                    <Send className="h-3.5 w-3.5" />
                    {LABELS.socialEmptyCta}
                  </Link>
                </div>
              ) : (
                <div>
                  {/* Event filter pills */}
                  <div className="flex items-center gap-1.5 mb-3">
                    {[
                      { key: 'all', label: 'Tous' },
                      { key: 'new_vehicle', label: 'Nouveaux' },
                      { key: 'price_reduction', label: 'Promo' },
                      { key: 'sold_vehicle', label: 'Vendus' },
                    ].filter(f => f.key === 'all' || eventCounts[f.key] > 0).map(f => (
                      <button
                        key={f.key}
                        onClick={() => { setEventFilter(f.key); setPublishLimit(6); }}
                        className={`px-2.5 py-1 rounded-full text-[11px] font-medium transition-all ${
                          eventFilter === f.key
                            ? 'bg-theme-inverted text-theme-inverted'
                            : 'bg-dark-hover text-secondary hover:bg-dark-surface'
                        }`}
                      >
                        {f.label} ({eventCounts[f.key]})
                      </button>
                    ))}
                  </div>

                  <div className="grid grid-cols-2 lg:grid-cols-3 gap-2.5">
                    {filteredByEvent.slice(0, publishLimit).map(vehicle => {
                      const eventType = getVehicleEventType(vehicle);
                      return (
                        <ReadyVehicleCard
                          key={vehicle.id}
                          vehicle={vehicle}
                          publications={publications}
                          activeChannels={getActiveChannels(eventType)}
                          eventType={eventType}
                          onPreview={handlePreviewChannel}
                          onPublishChannel={handlePublishChannel}
                          onPublishBatch={handlePublishBatch}
                        />
                      );
                    })}
                  </div>
                  {filteredByEvent.length > publishLimit && (
                    <button
                      onClick={() => setPublishLimit(prev => prev + 6)}
                      className="mt-3 w-full py-2 text-xs font-medium text-carmin hover:text-carmin-light bg-carmin/5 hover:bg-carmin/10 rounded-lg transition-colors"
                    >
                      Voir plus ({filteredByEvent.length - publishLimit} restants)
                    </button>
                  )}
                </div>
              )}
            </div>
          )}

          {/* PROGRAMMÉES */}
          {computedTab === 'scheduled' && (
            <div>
              {scheduledPubs.length > 0 ? (
                <div className="space-y-1.5">
                  {scheduledPubs.map(pub => {
                    const vehicle = vehicleMap[pub.vehicle_id];
                    return (
                      <ScheduledPubCard
                        key={pub.id}
                        pub={pub}
                        vehicle={vehicle}
                        onPreview={setPreviewVehicle}
                        onCancel={handleCancelScheduled}
                        onReschedule={handleRescheduleScheduled}
                      />
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Clock className="h-8 w-8 text-dimmed mx-auto mb-2" />
                  <p className="text-xs text-muted">{LABELS.noScheduledPubs}</p>
                </div>
              )}
            </div>
          )}

          {/* HISTORIQUE — paginé + véhicules cliquables */}
          {computedTab === 'history' && (
            <div>
              {/* Platform filter pills */}
              <div className="flex items-center gap-1.5 mb-3">
                {['all', 'instagram', 'facebook'].map(f => (
                  <button
                    key={f}
                    onClick={() => { setSocialFilter(f); setHistoryLimit(20); }}
                    className={`px-2.5 py-1 rounded-full text-[11px] font-medium transition-all ${
                      socialFilter === f
                        ? 'bg-theme-inverted text-theme-inverted'
                        : 'bg-dark-hover text-secondary hover:bg-dark-surface'
                    }`}
                  >
                    {f === 'all' ? LABELS.filterAll : f === 'instagram' ? 'Instagram' : 'Facebook'}
                  </button>
                ))}
              </div>

              {filteredHistory.length === 0 ? (
                <div className="text-center py-8">
                  <Send className="h-8 w-8 text-dimmed mx-auto mb-2" />
                  <p className="text-xs text-muted">{LABELS.noPublications}</p>
                </div>
              ) : (
                <div>
                  <div className="space-y-1.5">
                    {filteredHistory.slice(0, historyLimit).map(pub => {
                      const vehicle = vehicleMap[pub.vehicle_id];
                      return (
                        <div key={pub.id} className="flex items-center gap-3 p-2.5 bg-dark-hover rounded-lg border border-dark-border">
                          <div className="w-8 h-8 rounded-lg overflow-hidden bg-dark-surface flex-shrink-0">
                            {pub.content?.images?.[0] ? (
                              <img src={pub.content.images[0]} alt="" className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-muted text-[10px]">-</div>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            {vehicle ? (
                              <Link to={`/vehicles/${vehicle.id}`} className="text-xs font-medium text-primary truncate block hover:text-carmin transition-colors">
                                {vehicle.make} {vehicle.model}
                              </Link>
                            ) : (
                              <p className="text-xs font-medium text-primary truncate">Véhicule</p>
                            )}
                            <div className="flex items-center gap-1.5 mt-0.5">
                              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium border border-gray-600 text-muted">
                                <PlatformIcon platform={pub.platform} className="w-2.5 h-2.5" />
                                {pub.format === 'story' ? LABELS.story : pub.platform === 'instagram' ? LABELS.feed : LABELS.post}
                              </span>
                              <span className="text-[10px] text-muted">{formatPastDate(pub.published_at)}</span>
                            </div>
                          </div>
                          {pub.result?.post_url && (
                            <a
                              href={pub.result.post_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex-shrink-0 text-carmin hover:text-carmin-light"
                            >
                              <ExternalLink className="h-3.5 w-3.5" />
                            </a>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  {filteredHistory.length > historyLimit && (
                    <button
                      onClick={() => setHistoryLimit(prev => prev + 20)}
                      className="mt-3 w-full py-2 text-xs font-medium text-carmin hover:text-carmin-light bg-carmin/5 hover:bg-carmin/10 rounded-lg transition-colors"
                    >
                      Charger plus ({filteredHistory.length - historyLimit} restants)
                    </button>
                  )}
                </div>
              )}
            </div>
          )}

          {/* STATISTIQUES */}
          {computedTab === 'stats' && (
            <div>
              {statsLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-5 w-5 text-carmin animate-spin" />
                  <span className="ml-2 text-xs text-muted">Chargement des statistiques...</span>
                </div>
              ) : !stats || stats.total_publications === 0 ? (
                <div className="text-center py-8">
                  <BarChart3 className="h-8 w-8 text-dimmed mx-auto mb-2" />
                  <p className="text-sm font-medium text-primary mb-1">Aucune statistique disponible</p>
                  <p className="text-xs text-muted max-w-xs mx-auto">
                    Publiez du contenu sur Instagram ou Facebook pour voir les performances de vos publications.
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Engagement KPIs */}
                  <div>
                    <h3 className="text-xs font-medium text-muted mb-2">Engagement · 30 derniers jours</h3>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                      <div className="flex flex-col items-center p-2.5 bg-dark-surface rounded-lg border border-dark-border">
                        <Heart className="h-4 w-4 text-pink-400 mb-1" />
                        <span className="text-lg font-bold text-primary">{stats.totals.likes}</span>
                        <span className="text-[10px] text-muted">Likes</span>
                      </div>
                      <div className="flex flex-col items-center p-2.5 bg-dark-surface rounded-lg border border-dark-border">
                        <MessageCircle className="h-4 w-4 text-blue-400 mb-1" />
                        <span className="text-lg font-bold text-primary">{stats.totals.comments}</span>
                        <span className="text-[10px] text-muted">Commentaires</span>
                      </div>
                      <div className="flex flex-col items-center p-2.5 bg-dark-surface rounded-lg border border-dark-border">
                        <Bookmark className="h-4 w-4 text-amber-400 mb-1" />
                        <span className="text-lg font-bold text-primary">{stats.totals.saves}</span>
                        <span className="text-[10px] text-muted">Enregistrements</span>
                      </div>
                      <div className="flex flex-col items-center p-2.5 bg-dark-surface rounded-lg border border-dark-border">
                        <Users className="h-4 w-4 text-green-400 mb-1" />
                        <span className="text-lg font-bold text-primary">{stats.totals.reach}</span>
                        <span className="text-[10px] text-muted">Portée</span>
                      </div>
                    </div>
                    {stats.totals.impressions > 0 && (
                      <p className="text-[10px] text-muted mt-1.5 text-center">
                        {stats.totals.impressions.toLocaleString('fr-FR')} impressions · {stats.total_publications} publications
                        {stats.totals.likes + stats.totals.comments > 0 && stats.totals.impressions > 0 && (
                          <> · Taux d'engagement : {(((stats.totals.likes + stats.totals.comments) / stats.totals.impressions) * 100).toFixed(1)}%</>
                        )}
                      </p>
                    )}
                    {stats.totals.impressions === 0 && (
                      <p className="text-[10px] text-muted mt-1.5 text-center">
                        {stats.total_publications} publications · {stats.totals.likes + stats.totals.comments} interactions
                      </p>
                    )}
                  </div>

                  {/* Platform breakdown */}
                  <div>
                    <h3 className="text-xs font-medium text-muted mb-2">Par plateforme</h3>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="p-3 bg-dark-surface rounded-lg border border-dark-border">
                        <div className="flex items-center gap-2 mb-2">
                          <PlatformIcon platform="instagram" className="w-3.5 h-3.5 text-pink-400" />
                          <span className="text-xs font-medium text-primary">Instagram</span>
                          <span className="text-[10px] text-muted ml-auto">{stats.platforms.instagram?.count || 0} posts</span>
                        </div>
                        <div className="flex items-center gap-3 text-[11px]">
                          <span className="flex items-center gap-1 text-muted">
                            <Heart className="w-3 h-3 text-pink-400/60" />
                            {stats.platforms.instagram?.likes || 0}
                          </span>
                          <span className="flex items-center gap-1 text-muted">
                            <MessageCircle className="w-3 h-3 text-blue-400/60" />
                            {stats.platforms.instagram?.comments || 0}
                          </span>
                          {(stats.platforms.instagram?.reach || 0) > 0 && (
                            <span className="flex items-center gap-1 text-muted">
                              <Users className="w-3 h-3 text-green-400/60" />
                              {stats.platforms.instagram.reach}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="p-3 bg-dark-surface rounded-lg border border-dark-border">
                        <div className="flex items-center gap-2 mb-2">
                          <PlatformIcon platform="facebook" className="w-3.5 h-3.5 text-blue-400" />
                          <span className="text-xs font-medium text-primary">Facebook</span>
                          <span className="text-[10px] text-muted ml-auto">{stats.platforms.facebook?.count || 0} posts</span>
                        </div>
                        <div className="flex items-center gap-3 text-[11px]">
                          <span className="flex items-center gap-1 text-muted">
                            <Heart className="w-3 h-3 text-pink-400/60" />
                            {stats.platforms.facebook?.likes || 0}
                          </span>
                          <span className="flex items-center gap-1 text-muted">
                            <MessageCircle className="w-3 h-3 text-blue-400/60" />
                            {stats.platforms.facebook?.comments || 0}
                          </span>
                          <span className="flex items-center gap-1 text-muted">
                            <Share2 className="w-3 h-3 text-green-400/60" />
                            {stats.platforms.facebook?.shares || 0}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Per-post performance */}
                  {stats.publications?.length > 0 && (
                    <div>
                      <h3 className="text-xs font-medium text-muted mb-2">Performance par publication</h3>
                      <div className="space-y-1.5">
                        {stats.publications.map(pub => {
                          const vehicle = vehicleMap[pub.vehicle_id];
                          const ins = pub.insights || {};
                          const totalEngagement = (ins.likes || 0) + (ins.comments || 0) + (ins.shares || 0) + (ins.saves || 0);
                          return (
                            <div key={pub.id} className="flex items-center gap-3 p-2.5 bg-dark-hover rounded-lg border border-dark-border">
                              <div className="w-8 h-8 rounded-lg overflow-hidden bg-dark-surface flex-shrink-0">
                                {pub.content?.images?.[0] ? (
                                  <img src={pub.content.images[0]} alt="" className="w-full h-full object-cover" />
                                ) : (
                                  <div className="w-full h-full flex items-center justify-center text-muted text-[10px]">-</div>
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-xs font-medium text-primary truncate">
                                  {vehicle ? `${vehicle.make} ${vehicle.model}` : 'Véhicule'}
                                </p>
                                <div className="flex items-center gap-1.5 mt-0.5">
                                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium border border-gray-600 text-muted">
                                    <PlatformIcon platform={pub.platform} className="w-2.5 h-2.5" />
                                    {pub.format === 'story' ? 'Story' : pub.platform === 'instagram' ? 'Feed' : 'Post'}
                                  </span>
                                  <span className="text-[10px] text-muted">{formatPastDate(pub.published_at)}</span>
                                </div>
                              </div>
                              <div className="flex items-center gap-2.5 flex-shrink-0">
                                {(ins.likes || 0) > 0 && (
                                  <span className="flex items-center gap-0.5 text-[11px] text-muted" title="Likes">
                                    <Heart className="w-3 h-3 text-pink-400/70" />
                                    {ins.likes}
                                  </span>
                                )}
                                {(ins.comments || 0) > 0 && (
                                  <span className="flex items-center gap-0.5 text-[11px] text-muted" title="Commentaires">
                                    <MessageCircle className="w-3 h-3 text-blue-400/70" />
                                    {ins.comments}
                                  </span>
                                )}
                                {(ins.reach || 0) > 0 && (
                                  <span className="flex items-center gap-0.5 text-[11px] text-muted" title="Portée">
                                    <Users className="w-3 h-3 text-green-400/70" />
                                    {ins.reach}
                                  </span>
                                )}
                                {totalEngagement === 0 && (
                                  <span className="text-[10px] text-dimmed">--</span>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* STATISTIQUES */}
          {computedTab === 'stats' && (
            <div>
              {statsLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-5 w-5 text-carmin animate-spin" />
                  <span className="ml-2 text-xs text-muted">Chargement des statistiques...</span>
                </div>
              ) : !stats || stats.total_publications === 0 ? (
                <div className="text-center py-8">
                  <BarChart3 className="h-8 w-8 text-dimmed mx-auto mb-2" />
                  <p className="text-sm font-medium text-primary mb-1">Aucune statistique disponible</p>
                  <p className="text-xs text-muted max-w-xs mx-auto">
                    Publiez du contenu sur Instagram ou Facebook pour voir les performances de vos publications.
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Engagement KPIs */}
                  <div>
                    <h3 className="text-xs font-medium text-muted mb-2">Engagement · 30 derniers jours</h3>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                      <div className="flex flex-col items-center p-2.5 bg-dark-surface rounded-lg border border-dark-border">
                        <Heart className="h-4 w-4 text-pink-400 mb-1" />
                        <span className="text-lg font-bold text-primary">{stats.totals.likes}</span>
                        <span className="text-[10px] text-muted">Likes</span>
                      </div>
                      <div className="flex flex-col items-center p-2.5 bg-dark-surface rounded-lg border border-dark-border">
                        <MessageCircle className="h-4 w-4 text-blue-400 mb-1" />
                        <span className="text-lg font-bold text-primary">{stats.totals.comments}</span>
                        <span className="text-[10px] text-muted">Commentaires</span>
                      </div>
                      <div className="flex flex-col items-center p-2.5 bg-dark-surface rounded-lg border border-dark-border">
                        <Bookmark className="h-4 w-4 text-amber-400 mb-1" />
                        <span className="text-lg font-bold text-primary">{stats.totals.saves}</span>
                        <span className="text-[10px] text-muted">Enregistrements</span>
                      </div>
                      <div className="flex flex-col items-center p-2.5 bg-dark-surface rounded-lg border border-dark-border">
                        <Users className="h-4 w-4 text-green-400 mb-1" />
                        <span className="text-lg font-bold text-primary">{stats.totals.reach}</span>
                        <span className="text-[10px] text-muted">Portée</span>
                      </div>
                    </div>
                    {stats.totals.impressions > 0 && (
                      <p className="text-[10px] text-muted mt-1.5 text-center">
                        {stats.totals.impressions.toLocaleString('fr-FR')} impressions · {stats.total_publications} publications
                        {stats.totals.likes + stats.totals.comments > 0 && stats.totals.impressions > 0 && (
                          <> · Taux d'engagement : {(((stats.totals.likes + stats.totals.comments) / stats.totals.impressions) * 100).toFixed(1)}%</>
                        )}
                      </p>
                    )}
                    {stats.totals.impressions === 0 && (
                      <p className="text-[10px] text-muted mt-1.5 text-center">
                        {stats.total_publications} publications · {stats.totals.likes + stats.totals.comments} interactions
                      </p>
                    )}
                  </div>

                  {/* Platform breakdown */}
                  <div>
                    <h3 className="text-xs font-medium text-muted mb-2">Par plateforme</h3>
                    <div className="grid grid-cols-2 gap-2">
                      {/* Instagram */}
                      <div className="p-3 bg-dark-surface rounded-lg border border-dark-border">
                        <div className="flex items-center gap-2 mb-2">
                          <PlatformIcon platform="instagram" className="w-3.5 h-3.5 text-pink-400" />
                          <span className="text-xs font-medium text-primary">Instagram</span>
                          <span className="text-[10px] text-muted ml-auto">{stats.platforms.instagram.count} posts</span>
                        </div>
                        <div className="flex items-center gap-3 text-[11px]">
                          <span className="flex items-center gap-1 text-muted">
                            <Heart className="w-3 h-3 text-pink-400/60" />
                            {stats.platforms.instagram.likes}
                          </span>
                          <span className="flex items-center gap-1 text-muted">
                            <MessageCircle className="w-3 h-3 text-blue-400/60" />
                            {stats.platforms.instagram.comments}
                          </span>
                          {stats.platforms.instagram.reach > 0 && (
                            <span className="flex items-center gap-1 text-muted">
                              <Users className="w-3 h-3 text-green-400/60" />
                              {stats.platforms.instagram.reach}
                            </span>
                          )}
                        </div>
                      </div>
                      {/* Facebook */}
                      <div className="p-3 bg-dark-surface rounded-lg border border-dark-border">
                        <div className="flex items-center gap-2 mb-2">
                          <PlatformIcon platform="facebook" className="w-3.5 h-3.5 text-blue-400" />
                          <span className="text-xs font-medium text-primary">Facebook</span>
                          <span className="text-[10px] text-muted ml-auto">{stats.platforms.facebook.count} posts</span>
                        </div>
                        <div className="flex items-center gap-3 text-[11px]">
                          <span className="flex items-center gap-1 text-muted">
                            <Heart className="w-3 h-3 text-pink-400/60" />
                            {stats.platforms.facebook.likes}
                          </span>
                          <span className="flex items-center gap-1 text-muted">
                            <MessageCircle className="w-3 h-3 text-blue-400/60" />
                            {stats.platforms.facebook.comments}
                          </span>
                          <span className="flex items-center gap-1 text-muted">
                            <Share2 className="w-3 h-3 text-green-400/60" />
                            {stats.platforms.facebook.shares}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Per-post performance */}
                  {stats.publications.length > 0 && (
                    <div>
                      <h3 className="text-xs font-medium text-muted mb-2">Performance par publication</h3>
                      <div className="space-y-1.5">
                        {stats.publications.map(pub => {
                          const vehicle = vehicleMap[pub.vehicle_id];
                          const ins = pub.insights || {};
                          const totalEngagement = (ins.likes || 0) + (ins.comments || 0) + (ins.shares || 0) + (ins.saves || 0);
                          return (
                            <div key={pub.id} className="flex items-center gap-3 p-2.5 bg-dark-hover rounded-lg border border-dark-border">
                              <div className="w-8 h-8 rounded-lg overflow-hidden bg-dark-surface flex-shrink-0">
                                {pub.content?.images?.[0] ? (
                                  <img src={pub.content.images[0]} alt="" className="w-full h-full object-cover" />
                                ) : (
                                  <div className="w-full h-full flex items-center justify-center text-muted text-[10px]">-</div>
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-xs font-medium text-primary truncate">
                                  {vehicle ? `${vehicle.make} ${vehicle.model}` : 'Véhicule'}
                                </p>
                                <div className="flex items-center gap-1.5 mt-0.5">
                                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium border border-gray-600 text-muted">
                                    <PlatformIcon platform={pub.platform} className="w-2.5 h-2.5" />
                                    {pub.format === 'story' ? 'Story' : pub.platform === 'instagram' ? 'Feed' : 'Post'}
                                  </span>
                                  <span className="text-[10px] text-muted">{formatPastDate(pub.published_at)}</span>
                                </div>
                              </div>
                              <div className="flex items-center gap-2.5 flex-shrink-0">
                                {(ins.likes || 0) > 0 && (
                                  <span className="flex items-center gap-0.5 text-[11px] text-muted" title="Likes">
                                    <Heart className="w-3 h-3 text-pink-400/70" />
                                    {ins.likes}
                                  </span>
                                )}
                                {(ins.comments || 0) > 0 && (
                                  <span className="flex items-center gap-0.5 text-[11px] text-muted" title="Commentaires">
                                    <MessageCircle className="w-3 h-3 text-blue-400/70" />
                                    {ins.comments}
                                  </span>
                                )}
                                {(ins.reach || 0) > 0 && (
                                  <span className="flex items-center gap-0.5 text-[11px] text-muted" title="Portée">
                                    <Users className="w-3 h-3 text-green-400/70" />
                                    {ins.reach}
                                  </span>
                                )}
                                {totalEngagement === 0 && (
                                  <span className="text-[10px] text-dimmed">--</span>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Preview Modal */}
      {previewVehicle && (
        <PublicationPreviewModal
          vehicle={previewVehicle.vehicle || previewVehicle}
          userSettings={settings}
          initialPlatform={previewVehicle.platform}
          initialFormat={previewVehicle.format}
          onClose={() => setPreviewVehicle(null)}
          onPublished={handlePublished}
          onDeleted={handlePublished}
        />
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main Social page
// ---------------------------------------------------------------------------
const Social = () => {
  const [settings, setSettings] = useState(null);
  const [oauthAccounts, setOauthAccounts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get('/settings').then(r => r.data).catch(() => null),
      api.get('/oauth/accounts').then(r => r.data?.accounts || []).catch(() => []),
    ]).then(([s, accounts]) => {
      setSettings(s);
      setOauthAccounts(accounts);
      setLoading(false);
    });
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="h-6 w-6 border-2 border-carmin/30 border-t-carmin rounded-full animate-spin" />
      </div>
    );
  }

  const hasConnectedAccounts = oauthAccounts.some(
    a => (a.platform === 'instagram' || a.platform === 'facebook')
  );

  return hasConnectedAccounts
    ? <SocialDashboard oauthAccounts={oauthAccounts} settings={settings} />
    : <SocialOnboarding />;
};

export default Social;
